import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/testimonials")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const showEmpty = !loading && items.length === 0;

  return (
    <div data-testid="testimonials-page" className="bg-[#1A1514]">
      <section className="pt-40 md:pt-48 px-6 md:px-12 lg:px-16 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">Chapter III</p>
        </Reveal>
        <Reveal delay={150}>
          <h1
            data-testid="testimonials-title"
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl leading-none tracking-tighter text-ink-ivory"
          >
            <span className="italic font-light text-ink-champagne">Words.</span>
          </h1>
        </Reveal>
        <Reveal delay={350}>
          <p className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-ink-ivory/65">
            Quiet sentences from the couples who let us into their day.
            Each is printed here exactly as written, with the same restraint we
            bring to our work.
          </p>
        </Reveal>
      </section>

      {showEmpty ? (
        <TestimonialsEmpty />
      ) : (
        <div>
          {(loading ? Array.from({ length: 2 }) : items).map((t, i) =>
            t ? <Quote key={t.id} t={t} index={i} /> : <SkeletonQuote key={i} />
          )}
        </div>
      )}
    </div>
  );
}

function Quote({ t, index }) {
  return (
    <section
      data-testid={`testimonial-${t.id}`}
      className="min-h-[54vh] md:min-h-[62vh] flex items-center px-6 md:px-12 lg:px-20 py-14 md:py-20"
    >
      <div className="max-w-[720px] mx-auto w-full">
        <Reveal>
          <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">
            {String(index + 1).padStart(2, "0")} — Testimonial
          </p>
        </Reveal>
        <Reveal delay={250}>
          <blockquote className="mt-8 md:mt-10">
            <p className="font-display text-xl md:text-[2rem] lg:text-[2.5rem] leading-[1.4] md:leading-[1.35] tracking-[-0.005em] text-ink-ivory">
              <span className="text-ink-champagne italic font-light">&ldquo;</span>
              {t.quote}
              <span className="text-ink-champagne italic font-light">&rdquo;</span>
            </p>
          </blockquote>
        </Reveal>
        <Reveal delay={500}>
          <footer className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-baseline gap-2 md:gap-8">
            <p className="font-display italic text-base md:text-lg text-ink-ivory">— {t.author}</p>
            {(t.role || t.location) && (
              <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
                {[t.role, t.location].filter(Boolean).join(" · ")}
              </p>
            )}
          </footer>
        </Reveal>
      </div>
    </section>
  );
}

function SkeletonQuote() {
  return (
    <section className="min-h-[54vh] md:min-h-[62vh] flex items-center px-6 md:px-12 lg:px-20 py-14 md:py-20">
      <div className="max-w-[720px] mx-auto w-full space-y-5">
        <div className="h-3 w-32 bg-ink-ivory/10 animate-pulse" />
        <div className="h-7 md:h-9 w-full bg-ink-ivory/10 animate-pulse" />
        <div className="h-7 md:h-9 w-5/6 bg-ink-ivory/10 animate-pulse" />
      </div>
    </section>
  );
}

function TestimonialsEmpty() {
  return (
    <section data-testid="testimonials-empty" className="min-h-[60vh] flex items-center px-6 md:px-24 py-32">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-[10px] tracking-[0.42em] uppercase text-ink-champagne">In Preparation</p>
        <h2 className="mt-8 font-display text-3xl md:text-5xl leading-tight tracking-tight text-ink-ivory">
          The kindest sentences arrive slowly.
        </h2>
        <p className="mt-10 text-base text-ink-ivory/65 leading-relaxed">
          The studio is gathering letters from this season&rsquo;s couples.
          They will be printed here as they are received.
        </p>
      </div>
    </section>
  );
}
